function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useRef, useState, useContext, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Button } from '../../../Button';
import { Chip } from '../../../Chip';
import { Tag } from '../../../Tag';
import { Tooltip } from '../../../Tooltip';
import { ENTER_KEY, SPACE_KEYCODE } from '../../../Utils/constant';
import { isHeaderOverlayOpen } from '../../../Utils/helper';
import { SelectMenuContext } from '../../context';
import { SelectMenuTypes, removePlaceholder, defaultSelectedValue } from '../../helper';
import styles from '@dtsl/css-select-menu/dist/index.module.css';
const SelectMenuControl = props => {
  const ref = useRef(null);
  const contentPrimaryInfoRef = useRef(null);
  const {
    isOpen,
    setIsOpen,
    isOverlayOpen,
    setIsOverlayOpen,
    selectedValues,
    setSelectedValues,
    isMobile,
    searchRef,
    searchValue,
    handleSearch,
    isControlSearchable,
    isPanelSearchable,
    selectMenuControlRef,
    isSearchable,
    floatingUiProps
  } = useContext(SelectMenuContext);
  const {
    readonly,
    disabled,
    tooltipData,
    selectMenuType,
    placeHolder,
    arrowClassName,
    isControlButton,
    controlClassName,
    placeholderClassName,
    controlButtonMetaData,
    dataTestId,
    maxTagWidth,
    countTagWidth,
    isControlChip,
    controlChipMetaData
  } = props;
  const MAX_TAG_WIDTH = maxTagWidth || 100;
  const COUNT_TAG_WIDTH = countTagWidth || 135;
  const filteredSelectedValues = removePlaceholder(selectedValues, placeHolder);
  const filteredSelectedValuesCount = filteredSelectedValues.length;
  const [noOfTags, setNoOfTags] = useState(0);
  const [visible, setVisible] = useState(false);
  const [controlTooltipVisible, setControlTooltipVisible] = useState(false);
  const isOpenRef = React.useRef(isMobile ? isOverlayOpen : isOpen);
  isOpenRef.current = isMobile ? isOverlayOpen : isOpen;
  const {
    text: tooltipText,
    timeout = 200,
    placement = 'bottom',
    offset: tooltipOffset = [0, 8]
  } = tooltipData || {};
  const calculateNoofTags = () => {
    var _ref$current;
    if (ref !== null && ref !== void 0 && ref.current && ref !== null && ref !== void 0 && (_ref$current = ref.current) !== null && _ref$current !== void 0 && _ref$current.offsetWidth) {
      setNoOfTags(Math.floor((ref.current.offsetWidth - COUNT_TAG_WIDTH) / MAX_TAG_WIDTH));
    }
  };
  useEffect(() => {
    calculateNoofTags();
    window.addEventListener('resize', calculateNoofTags);
    return () => {
      window.removeEventListener('resize', calculateNoofTags);
    };
  }, []);
  useEffect(() => {
    if (isSearchable && isOpen) {
      var _searchRef$current, _selectMenuControlRef;
      (_searchRef$current = searchRef.current) === null || _searchRef$current === void 0 ? void 0 : _searchRef$current.focus();
      (_selectMenuControlRef = selectMenuControlRef.current) === null || _selectMenuControlRef === void 0 ? void 0 : _selectMenuControlRef.classList.add(styles['sib-selectmenu-control-focused']);
    }
  }, [controlTooltipVisible]);
  useEffect(() => {
    if (isMobile) {
      return;
    }
    const updateVisible = element => {
      if (!element) {
        return;
      }
      if (element.offsetWidth < element.scrollWidth && !isOpen) {
        setControlTooltipVisible(true);
        return;
      }
      setVisible(false);
      setControlTooltipVisible(false);
    };
    updateVisible(contentPrimaryInfoRef.current || ref.current);
  }, [isMobile, isOpen]);
  const isValueSelected = () => {
    return (selectedValues[0] || {}).value !== '';
  };
  const controlClass = classNames({
    [styles['sib-selectmenu-control']]: true,
    [styles['sib-selectmenu-control__readonly']]: readonly,
    [styles['sib-selectmenu-disabled']]: disabled,
    [controlClassName]: !!controlClassName
  });
  const placeholderClass = classNames({
    [styles['is-selected']]: isValueSelected(),
    [styles['sib-selectmenu-control__placeholder']]: true,
    [styles['search__placeholder--multiSelectOption']]: selectMenuType === SelectMenuTypes.MULTI_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_MULTI_SELECTION,
    [styles['search__placeholder--singleSelectOption']]: selectMenuType === SelectMenuTypes.SINGLE_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_SINGLE_SELECTION,
    [placeholderClassName]: !!placeholderClassName
  });
  const arrowClass = `${styles['sib-selectmenu-arrow']} ${arrowClassName}`;
  const removeTag = (event, tag) => {
    event.stopPropagation();
    event.preventDefault();
    const filteredValues = selectedValues.filter(selectedValue => selectedValue.label !== tag.label);
    setSelectedValues(filteredValues.length ? [...filteredValues] : [defaultSelectedValue(placeHolder)]);
    props.removeTag(tag, [...filteredValues]);
    if (isOpen || isOverlayOpen) {
      var _searchRef$current2, _selectMenuControlRef2;
      (_searchRef$current2 = searchRef.current) === null || _searchRef$current2 === void 0 ? void 0 : _searchRef$current2.focus();
      (_selectMenuControlRef2 = selectMenuControlRef.current) === null || _selectMenuControlRef2 === void 0 ? void 0 : _selectMenuControlRef2.classList.add(styles['sib-selectmenu-control-focused']);
    }
  };
  const handleOnClick = event => {
    event.stopPropagation();
    event.preventDefault();
    if (!disabled) {
      setVisible(false);
      if (isMobile) {
        if (isHeaderOverlayOpen() && !isOverlayOpen) {
          setTimeout(() => {
            if (!isOverlayOpen) {
              searchValue && handleSearch('');
            }
            setIsOverlayOpen(!isOverlayOpen);
          }, 500);
        } else {
          if (!isOverlayOpen) {
            searchValue && handleSearch('');
          }
          setIsOverlayOpen(!isOverlayOpen);
        }
      } else {
        if (!isOpen) {
          searchValue && handleSearch('');
        }
        setIsOpen(!isOpen);
      }
    }
  };
  const handleKeyDown = event => {
    if (event.key === ENTER_KEY || event.keyCode === SPACE_KEYCODE && !isControlSearchable) {
      isMobile ? setIsOverlayOpen(!isOverlayOpen) : setIsOpen(!isOpen);
    }
    if (event.keyCode === 40) {
      isMobile ? setIsOverlayOpen(true) : setIsOpen(true);
    }
  };
  const getPlaceHolderValue = () => {
    if (selectedValues.length > 0) {
      const {
        addtionalInfo = () => {},
        label,
        altLabel,
        svg
      } = selectedValues[0];
      const addInfo = selectMenuType === SelectMenuTypes.SINGLE_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_SINGLE_SELECTION ? addtionalInfo() : '';
      if (selectMenuType === SelectMenuTypes.MULTI_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_MULTI_SELECTION) {
        if (selectedValues.length === 1 && selectedValues[0].label === placeHolder && (!searchValue || isPanelSearchable) && !isControlSearchable) {
          return selectedValues[0].label;
        }
        return /*#__PURE__*/React.createElement(React.Fragment, null, filteredSelectedValues.slice(0, noOfTags).map(selectedValue => {
          const {
            label,
            altLabel
          } = selectedValue;
          const tagClass = classNames({
            [styles['sib-tag-selectmenu']]: true
          });
          return /*#__PURE__*/React.createElement(Tag, {
            key: `${selectedValue.key}_${selectedValue.label}`,
            label: altLabel ? altLabel : label,
            closable: true,
            onClose: event => removeTag(event, selectedValue),
            className: tagClass,
            showTooltip: true
          });
        }), filteredSelectedValuesCount > noOfTags ? /*#__PURE__*/React.createElement(Tag, {
          label: `+${filteredSelectedValuesCount - noOfTags}`
        }) : null);
      }
      if ((selectMenuType === SelectMenuTypes.SINGLE_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_SINGLE_SELECTION) && searchValue && !isPanelSearchable) {
        return;
      }
      return /*#__PURE__*/React.createElement("div", {
        className: styles['sib-selectmenu--selected-value']
      }, /*#__PURE__*/React.createElement("span", {
        className: styles['primary-info'],
        ref: contentPrimaryInfoRef
      }, svg && /*#__PURE__*/React.cloneElement(svg, {
        className: styles['primary-info__icon']
      }), altLabel ? altLabel : label), addInfo && /*#__PURE__*/React.createElement("span", {
        className: styles['additional-info']
      }, addInfo));
    }
  };
  const {
    refs
  } = floatingUiProps;
  const getControlTrigger = () => {
    if (isControlButton) {
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Button, _extends({}, controlButtonMetaData, {
        dropdownOpen: isMobile ? isOverlayOpen : isOpen,
        onClick: !readonly ? handleOnClick : () => {},
        disabled: disabled,
        ref: refs.setReference,
        isFloatingComp: true
      })));
    }
    if (isControlChip) {
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Chip, _extends({}, controlChipMetaData, {
        variant: "selectableWithPanel",
        dropdownOpen: isMobile ? isOverlayOpen : isOpen,
        onClick: !readonly ? handleOnClick : () => {},
        disabled: disabled,
        ref: refs.setReference
      })));
    }
  };
  const selectMenuControl = !isControlButton && !isControlChip ? /*#__PURE__*/React.createElement("div", {
    ref: refs.setReference
  }, /*#__PURE__*/React.createElement("div", {
    ref: selectMenuControlRef,
    disabled: disabled,
    className: controlClass,
    onClick: !readonly ? handleOnClick : () => {},
    onKeyDown: !readonly ? handleKeyDown : () => {},
    "aria-haspopup": "listbox",
    "data-testid": dataTestId ? dataTestId : null,
    tabIndex: !disabled ? '0' : null,
    role: "button"
  }, /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: placeholderClass
  }, getPlaceHolderValue(), isControlSearchable && /*#__PURE__*/React.createElement("div", {
    className: styles['input-wrapper'],
    "data-value": searchValue
  }, /*#__PURE__*/React.createElement("input", {
    ref: searchRef,
    placeholder: (selectMenuType === SelectMenuTypes.MULTI_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_MULTI_SELECTION) && selectedValues.length === 1 && selectedValues[0].label === placeHolder ? placeHolder : '',
    className: `
                  ${(selectMenuType === SelectMenuTypes.MULTI_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_MULTI_SELECTION) && selectedValues.length > 1 ? styles['offset-input'] : ''}`,
    value: searchValue || '',
    onChange: event => {
      const searchedValue = event.target.value;
      handleSearch(searchedValue);
    },
    onFocus: event => {
      var _selectMenuControlRef3;
      event.stopPropagation();
      (_selectMenuControlRef3 = selectMenuControlRef.current) === null || _selectMenuControlRef3 === void 0 ? void 0 : _selectMenuControlRef3.classList.add(styles['sib-selectmenu-control-focused']);
    },
    onBlur: event => {
      var _selectMenuControlRef4;
      event.stopPropagation();
      (_selectMenuControlRef4 = selectMenuControlRef.current) === null || _selectMenuControlRef4 === void 0 ? void 0 : _selectMenuControlRef4.classList.remove(styles['sib-selectmenu-control-focused']);
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles['sib-selectmenu-arrow-wrapper']
  }, /*#__PURE__*/React.createElement("span", {
    className: arrowClass
  })))) : getControlTrigger();
  if (isMobile) {
    return selectMenuControl;
  }
  if (tooltipText && isControlButton) {
    return /*#__PURE__*/React.createElement(Tooltip, {
      text: tooltipText,
      offset: tooltipOffset,
      visible: !isOpenRef.current && visible,
      placement: placement,
      onMouseEnter: () => {
        setTimeout(() => {
          if (!isOpenRef.current) {
            setVisible(true);
          }
        }, timeout);
      },
      onMouseLeave: () => {
        setTimeout(() => {
          setVisible(false);
        }, timeout);
      }
    }, selectMenuControl);
  }
  if (controlTooltipVisible) {
    if (contentPrimaryInfoRef.current) {
      return /*#__PURE__*/React.createElement(Tooltip, {
        text: contentPrimaryInfoRef.current.innerText
      }, selectMenuControl);
    } else {
      return /*#__PURE__*/React.createElement(Tooltip, {
        text: getPlaceHolderValue()
      }, selectMenuControl);
    }
  }
  return selectMenuControl;
};
SelectMenuControl.propTypes = {
  maxTagWidth: PropTypes.number,
  countTagWidth: PropTypes.number,
  tooltipData: PropTypes.object.isRequired,
  readonly: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  removeTag: PropTypes.func.isRequired,
  placeholderClassName: PropTypes.string.isRequired,
  selectMenuType: PropTypes.string.isRequired,
  placeHolder: PropTypes.string.isRequired,
  controlClassName: PropTypes.string.isRequired,
  arrowClassName: PropTypes.string.isRequired,
  isControlButton: PropTypes.bool.isRequired,
  controlButtonMetaData: PropTypes.object.isRequired,
  dataTestId: PropTypes.string,
  isControlChip: PropTypes.bool,
  controlChipMetaData: PropTypes.object
};
export default SelectMenuControl;